import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import type { TutorialSplashId } from '../onboarding/tutorialSplash/tutorialSplashIds';
import {
  DEFAULT_SETTINGS,
  fetchSettings,
  type UserSettings,
} from './settingsApi';

export type UseUserSettingsResult = {
  settings: UserSettings;
  loading: boolean;
  applySettings: (next: UserSettings) => void;
  markTutorialSplashDismissed: (id: TutorialSplashId) => void;
  refresh: () => Promise<void>;
};

const UserSettingsContext = createContext<UseUserSettingsResult | undefined>(
  undefined,
);

// Single fetch shared by every board/page that needs theme and SRS preferences.
export const UserSettingsProvider = ({ children }: { children: ReactNode }) => {
  const { authChecked, user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setSettings(await fetchSettings());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authChecked) {
      return;
    }

    if (!user?.id) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void fetchSettings()
      .then((loaded) => {
        if (!cancelled) {
          setSettings(loaded);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authChecked, user?.id]);

  const applySettings = useCallback((next: UserSettings) => {
    setSettings(next);
  }, []);

  const markTutorialSplashDismissed = useCallback((id: TutorialSplashId) => {
    setSettings((prev) => ({
      ...prev,
      tutorialSplashes: {
        ...prev.tutorialSplashes,
        [id]: false,
      },
    }));
  }, []);

  return (
    <UserSettingsContext.Provider
      value={{
        settings,
        loading,
        applySettings,
        markTutorialSplashDismissed,
        refresh,
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};

export function useUserSettings(): UseUserSettingsResult {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error(
      'useUserSettings must be used within a UserSettingsProvider',
    );
  }
  return context;
}
